import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import EmojiPicker from '@/components/EmojiPicker';
import { HarvestPhotoPicker, PostPhoto } from '@/components/PhotoUpload';
import {
  MessageCircleQuestion, Search, Plus, MapPin, Sprout, CheckCircle2, Leaf,
  ChevronLeft, ChevronRight, X, ThumbsUp, Award, ShieldCheck, HelpCircle, Send, Sparkles, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_META = {
  open: { label: 'Open', cls: 'bg-[#E5EBDD] text-[#7A6B57]' },
  answered: { label: 'Answered', cls: 'bg-[#8FB4D6]/20 text-[#3F6E96]' },
  resolved: { label: 'Solved', cls: 'bg-[#6B9F4A]/20 text-[#4E7A33]' },
  outcome_confirmed: { label: 'Solved · outcome confirmed', cls: 'bg-[#6B9F4A]/25 text-[#3F6127]' },
};
const PROV_META = {
  grew_this_crop: { label: 'Grows this crop', icon: Leaf },
  same_region: { label: 'Same region', icon: MapPin },
  verified_pro: { label: 'Verified grower', icon: ShieldCheck },
};

function StateBadge({ state }) {
  const m = STATE_META[state] || STATE_META.open;
  return <span data-testid={`state-${state}`} className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

function GardenContextCard({ fields, editable, onRemove }) {
  if (!fields || fields.length === 0) return null;
  return (
    <div className="bg-[#1A4331]/[0.04] border border-[#1A4331]/10 rounded-2xl p-4" data-testid="garden-context-card">
      <div className="flex items-center gap-1.5 mb-2">
        <Sprout className="w-3.5 h-3.5 text-[#6B9F4A]" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-[#1A4331]">Garden context</span>
        {editable && <span className="text-[10px] text-[#7A6B57] ml-auto">Shared with this post · tap ✕ to remove a detail</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {fields.map((f) => (
          <span key={f.key} data-testid={`context-field-${f.key}`} className="inline-flex items-center gap-1 bg-white border border-[#1A4331]/10 rounded-full pl-2.5 pr-2 py-1 text-xs">
            <span className="text-[#7A6B57]">{f.label}:</span>
            <span className="font-semibold text-[#1A4331]">{f.value}</span>
            <span className={`text-[9px] uppercase ml-0.5 ${f.source === 'user' ? 'text-[#C9A85B]' : 'text-[#8EAA97]'}`}>{f.source === 'user' ? 'you' : 'auto'}</span>
            {editable && <button onClick={() => onRemove(f.key)} data-testid={`remove-field-${f.key}`} className="ml-0.5 text-[#7A6B57] hover:text-[#C66B4A]"><X className="w-3 h-3" /></button>}
          </span>
        ))}
      </div>
    </div>
  );
}

function AskModal({ meta, onClose, onCreated, initialTitle }) {
  const [crops, setCrops] = useState([]);
  const [profile, setProfile] = useState(null);
  const [cropId, setCropId] = useState(null);
  const [cropName, setCropName] = useState('');
  const [fields, setFields] = useState([]);
  const [title, setTitle] = useState(initialTitle || '');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('unknown');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [visibility, setVisibility] = useState('public');
  const [similar, setSimilar] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/community/qa/context`, { withCredentials: true })
      .then((r) => { setCrops(r.data.crops || []); setProfile(r.data.profile || null); })
      .catch(() => {});
  }, []);

  const pickCrop = async (c) => {
    setCropId(c.crop_id); setCropName(c.crop_name);
    try {
      const r = await axios.get(`${API}/community/qa/context`, { params: { crop_id: c.crop_id, planting_id: c.planting_id }, withCredentials: true });
      setFields(r.data.context_fields || []);
    } catch { /* ignore */ }
  };

  // debounced search-before-post + category suggest
  useEffect(() => {
    const t = setTimeout(() => {
      const text = `${title} ${body}`.trim();
      if (text.length < 4) { setSimilar([]); return; }
      axios.get(`${API}/community/qa/search`, { params: { text: title || body, crop_id: cropId || undefined, limit: 4 }, withCredentials: true })
        .then((r) => setSimilar(r.data || [])).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [title, body, cropId, category, meta]);

  const removeField = (key) => setFields((fs) => fs.filter((f) => f.key !== key));

  const submit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/community/qa/questions`, {
        title, body, crop_id: cropId, crop_name: cropName,
        issue_category: category === 'unknown' ? undefined : category,
        context_fields: fields, photo_urls: photoUrl ? [photoUrl] : [], visibility,
      }, { withCredentials: true });
      onCreated(r.data.id);
    } catch (e) { console.error(e); setSubmitting(false); }
  };

  const reuse = async (qid) => {
    try { await axios.post(`${API}/community/qa/questions/${qid}/reuse`, {}, { withCredentials: true }); } catch { /* ignore */ }
    onCreated(qid);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ask-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Ask the community</h3>
          <button onClick={onClose} className="text-[#7A6B57] hover:text-[#1A4331] p-1"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#7A6B57] mb-4">Your garden details are attached automatically so growers can help fast.</p>

        {/* crop quick picks */}
        <label className="block text-xs font-bold uppercase tracking-wider text-[#7A6B57] mb-2">Which crop?</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {crops.map((c) => (
            <button key={c.planting_id || c.crop_id} onClick={() => pickCrop(c)} data-testid={`ask-crop-${c.crop_id}`}
              className={`text-sm px-3 py-1.5 rounded-full font-medium transition-colors ${cropId === c.crop_id ? 'bg-[#1A4331] text-white' : 'bg-white text-[#1A4331] border border-[#1A4331]/10 hover:bg-[#F0F2EB]'}`}>
              {c.crop_name}
            </button>
          ))}
          {crops.length === 0 && <span className="text-sm text-[#7A6B57]">No crops in your garden yet — you can still ask below.</span>}
        </div>

        {fields.length > 0 && <div className="mb-4"><GardenContextCard fields={fields} editable onRemove={removeField} /></div>}

        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Your question (e.g. Why are my tomato leaves spotting?)" data-testid="ask-title"
          className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] mb-3" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Add detail — what you're seeing, what you've tried…" data-testid="ask-body"
          className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331] mb-1" />
        <div className="flex justify-end mb-3"><EmojiPicker testId="ask-emoji" onSelect={(e) => setBody((b) => b + e)} /></div>

        <label className="block text-xs font-bold uppercase tracking-wider text-[#7A6B57] mb-2">Issue type</label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(meta?.issue_categories || []).map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`ask-cat-${c.id}`}
              className={`text-xs px-2.5 py-1 rounded-full font-medium ${category === c.id ? 'bg-[#6B9F4A] text-white' : 'bg-white text-[#7A6B57] border border-[#1A4331]/10'}`}>
              {c.label}
            </button>
          ))}
        </div>

        <HarvestPhotoPicker photoUrl={photoUrl} onChange={setPhotoUrl} />

        {/* search-before-post */}
        {similar.length > 0 && (
          <div className="mt-3 bg-[#C9A85B]/10 border border-[#C9A85B]/30 rounded-2xl p-3" data-testid="similar-results">
            <p className="text-xs font-bold text-[#1A4331] mb-2 flex items-center gap-1"><Search className="w-3.5 h-3.5" /> This may already be answered</p>
            <div className="space-y-1.5">
              {similar.map((s) => (
                <div key={s.id} className="flex items-center gap-2 bg-white rounded-xl p-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1A4331] truncate">{s.title}</p>
                    <p className="text-[10px] text-[#7A6B57]">{s.crop_name || '—'} · {STATE_META[s.state]?.label}</p>
                  </div>
                  {(s.state === 'resolved' || s.state === 'outcome_confirmed') && (
                    <button onClick={() => reuse(s.id)} data-testid={`reuse-${s.id}`} className="text-[11px] font-semibold text-[#4E7A33] whitespace-nowrap">That solved it →</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid="ask-visibility" className="px-3 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 text-sm outline-none">
            <option value="public">Public</option>
            <option value="region">Region only</option>
          </select>
          <button onClick={submit} disabled={submitting || !title.trim()} data-testid="ask-submit"
            className="flex-1 bg-[#1A4331] hover:bg-[#0F2D1F] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-50">
            {submitting ? 'Posting…' : 'Post question'}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionDetail({ qid, user, onBack }) {
  const [data, setData] = useState(null);
  const [answer, setAnswer] = useState('');
  const [posting, setPosting] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/community/qa/questions/${qid}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { console.error(e); }
  }, [qid]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-16 text-center text-[#7A6B57]" data-testid="qa-detail-loading">Loading…</div>;
  const q = data.question;
  const owner = data.is_owner;

  const postAnswer = async () => {
    if (!answer.trim()) return;
    setPosting(true);
    try { await axios.post(`${API}/community/qa/questions/${qid}/answers`, { body: answer }, { withCredentials: true }); setAnswer(''); await load(); }
    catch (e) { console.error(e); } finally { setPosting(false); }
  };
  const helpful = async (aid) => { try { await axios.post(`${API}/community/qa/answers/${aid}/helpful`, {}, { withCredentials: true }); await load(); } catch { /* ignore */ } };
  const select = async (aid) => { try { await axios.post(`${API}/community/qa/questions/${qid}/select`, { answer_id: aid }, { withCredentials: true }); await load(); } catch { /* ignore */ } };
  const recordOutcome = async (result) => { try { await axios.post(`${API}/community/qa/questions/${qid}/outcome`, { result }, { withCredentials: true }); setOutcomeOpen(false); await load(); } catch { /* ignore */ } };
  const reopen = async () => { try { await axios.post(`${API}/community/qa/questions/${qid}/reopen`, {}, { withCredentials: true }); await load(); } catch { /* ignore */ } };

  return (
    <div className="space-y-4" data-testid="qa-detail">
      <button onClick={onBack} data-testid="qa-back" className="flex items-center gap-1 text-sm font-semibold text-[#7A6B57] hover:text-[#1A4331]"><ChevronLeft className="w-4 h-4" /> Back to questions</button>

      <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <StateBadge state={q.state} />
          <span className="text-[10px] font-semibold text-[#7A6B57] bg-[#E5EBDD] px-2 py-0.5 rounded-full uppercase">{q.issue_category}</span>
          {q.crop_name && <span className="text-[10px] font-semibold text-[#4E7A33] bg-[#6B9F4A]/12 px-2 py-0.5 rounded-full">{q.crop_name}</span>}
        </div>
        <h2 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{q.title}</h2>
        <p className="text-xs text-[#7A6B57] mt-1">{q.author_name} · {q.region || 'Anonymous region'}</p>
        {q.body && <p className="text-sm text-[#2A3528] mt-3 leading-relaxed">{q.body}</p>}
        <PostPhoto photoUrls={q.photo_urls} cropName={q.crop_name} postId={q.id} />
        {q.context_snapshot?.fields?.length > 0 && <div className="mt-4"><GardenContextCard fields={q.context_snapshot.fields} /></div>}

        {/* owner controls */}
        {owner && (q.state === 'resolved' || q.state === 'outcome_confirmed') && !q.outcome && (
          <div className="mt-4 bg-[#6B9F4A]/10 border border-[#6B9F4A]/30 rounded-2xl p-3" data-testid="outcome-prompt">
            <p className="text-sm font-semibold text-[#1A4331] mb-2">Did this solve it?</p>
            <div className="flex flex-wrap gap-2">
              {[['solved', 'Yes'], ['partly', 'Partly'], ['no', 'No'], ['too_early', 'Too early']].map(([r, l]) => (
                <button key={r} onClick={() => recordOutcome(r)} data-testid={`outcome-${r}`} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-[#6B9F4A]/40 text-[#1A4331] hover:bg-[#6B9F4A]/10">{l}</button>
              ))}
            </div>
          </div>
        )}
        {q.outcome && (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#4E7A33]" data-testid="outcome-recorded">
            <CheckCircle2 className="w-4 h-4" /> Outcome: <b className="capitalize">{q.outcome.result.replace('_', ' ')}</b>
            {q.outcome.action_tried && <span className="text-[#7A6B57]">· tried: {q.outcome.action_tried}</span>}
          </div>
        )}
        {owner && q.state !== 'open' && (
          <button onClick={reopen} data-testid="qa-reopen" className="mt-3 text-xs font-semibold text-[#C66B4A] hover:underline">Reopen — not solved yet</button>
        )}
      </div>

      {/* answers */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#7A6B57] mb-2">{data.answers.length} {data.answers.length === 1 ? 'answer' : 'answers'}</h3>
        <div className="space-y-3">
          {data.answers.map((a) => (
            <div key={a.id} data-testid={`answer-${a.id}`} className={`rounded-2xl p-4 border ${a.is_selected ? 'bg-[#6B9F4A]/8 border-[#6B9F4A]/40' : 'bg-white border-[#1A4331]/8'}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="w-7 h-7 rounded-full bg-[#6B9F4A]/20 flex items-center justify-center text-[#1A4331] font-bold text-xs">{a.author_name?.[0]}</span>
                <span className="text-sm font-semibold text-[#1A4331]">{a.author_name}</span>
                {a.is_selected && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#4E7A33] bg-[#6B9F4A]/20 px-2 py-0.5 rounded-full"><Award className="w-3 h-3" /> Solution</span>}
                {(a.why_context || []).map((w) => { const M = PROV_META[w]; if (!M) return null; const Ic = M.icon; return (
                  <span key={w} data-testid={`prov-${a.id}-${w}`} className="inline-flex items-center gap-1 text-[10px] font-medium text-[#7A6B57] bg-[#F0F2EB] px-2 py-0.5 rounded-full"><Ic className="w-3 h-3" /> {M.label}</span>
                ); })}
              </div>
              <p className="text-sm text-[#2A3528] leading-relaxed mt-1">{a.body}</p>
              <div className="flex items-center gap-3 mt-3">
                <button onClick={() => helpful(a.id)} data-testid={`helpful-${a.id}`} className={`flex items-center gap-1 text-xs font-semibold ${a.voted_helpful ? 'text-[#4E7A33]' : 'text-[#7A6B57] hover:text-[#1A4331]'}`}>
                  <ThumbsUp className="w-3.5 h-3.5" /> Helpful{a.helpful_count > 0 ? ` · ${a.helpful_count}` : ''}
                </button>
                {owner && !a.is_selected && (q.state === 'open' || q.state === 'answered') && (
                  <button onClick={() => select(a.id)} data-testid={`select-${a.id}`} className="flex items-center gap-1 text-xs font-semibold text-[#4E7A33] hover:underline"><CheckCircle2 className="w-3.5 h-3.5" /> Mark as solution</button>
                )}
              </div>
            </div>
          ))}
          {data.answers.length === 0 && <p className="text-sm text-[#7A6B57] italic bg-[#FAF6EC] rounded-2xl p-6 text-center">No answers yet — be the first to help.</p>}
        </div>
      </div>

      {/* answer composer */}
      <div className="bg-[#FAF6EC] rounded-2xl p-4 border border-[#1A4331]/10">
        <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} placeholder="Share what worked for you — your experience helps." data-testid="answer-input"
          className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
        <div className="flex justify-end items-center gap-1 mt-2">
          <EmojiPicker testId="answer-emoji" onSelect={(e) => setAnswer((a) => a + e)} />
          <button onClick={postAnswer} disabled={posting || !answer.trim()} data-testid="answer-submit" className="flex items-center gap-1.5 bg-[#1A4331] hover:bg-[#0F2D1F] text-white font-semibold py-2 px-5 rounded-full text-sm disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {posting ? 'Posting…' : 'Post answer'}
          </button>
        </div>
      </div>
      {outcomeOpen && null}
    </div>
  );
}

function CommunityQA({ user }) {
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [mine, setMine] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [askSeed, setAskSeed] = useState('');
  const [unseen, setUnseen] = useState(0);
  useEffect(() => { axios.get(`${API}/community/qa/unseen`, { withCredentials: true }).then((r) => setUnseen(r.data?.count || 0)).catch(() => {}); }, [detailId]);

  const loadSuggested = useCallback(async () => {
    setSuggestLoading(true);
    try {
      const r = await axios.get(`${API}/community/qa/suggested`, { withCredentials: true });
      setSuggested(r.data?.questions || []);
    } catch { setSuggested([]); }
    finally { setSuggestLoading(false); }
  }, []);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (stateFilter !== 'all') params.state = stateFilter;
      if (mine) params.mine = true;
      const url = search.trim() ? `${API}/community/qa/search` : `${API}/community/qa/questions`;
      if (search.trim()) params.text = search.trim();
      const r = await axios.get(url, { params, withCredentials: true });
      setQuestions(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [category, stateFilter, mine, search]);

  useEffect(() => { axios.get(`${API}/community/qa/meta`, { withCredentials: true }).then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSuggested(); }, [loadSuggested]);
  useEffect(() => { const t = setTimeout(loadQuestions, search ? 350 : 0); return () => clearTimeout(t); }, [loadQuestions, search]);

  if (detailId) {
    return <QuestionDetail qid={detailId} user={user} onBack={() => { setDetailId(null); loadQuestions(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="community-qa">
      {unseen > 0 && !mine && (
        <button onClick={() => { setMine(true); setStateFilter('all'); setSearch(''); setUnseen(0); }} data-testid="qa-unseen-banner"
          className="hg-press w-full flex items-center gap-2 bg-[#6B9F4A]/12 border border-[#6B9F4A]/30 rounded-2xl px-4 py-3 text-left">
          <span className="w-2.5 h-2.5 rounded-full bg-[#C66B4A] flex-shrink-0" />
          <span className="flex-1 text-sm text-[#1A4331] font-medium">{unseen === 1 ? 'You have a new answer' : `You have ${unseen} new answers`} — tap to see your questions</span>
          <ChevronRight className="w-4 h-4 text-[#1A4331]" />
        </button>
      )}
      {/* search + ask */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-[#7A6B57] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions & solved answers…" data-testid="qa-search"
            className="w-full pl-9 pr-3 py-2.5 bg-[#FAF6EC] rounded-full border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
        </div>
        <button onClick={() => { setAskSeed(''); setAskOpen(true); }} data-testid="qa-ask-button" className="bg-[#1A4331] hover:bg-[#0F2D1F] text-white font-semibold py-2.5 px-5 rounded-full flex items-center gap-1.5 whitespace-nowrap"><Plus className="w-4 h-4" /> Ask</button>
      </div>

      {/* AI-suggested starter questions — tap to ask, freshly generated each visit */}
      {(suggestLoading || suggested.length > 0) && (
        <div className="bg-[#B4D330]/12 rounded-2xl p-3 border border-[#B4D330]/30" data-testid="qa-suggested">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[#1A4331] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#4E7A33]" /> Not sure what to ask?</span>
            <button onClick={loadSuggested} data-testid="qa-suggested-refresh" className="text-[11px] font-semibold text-[#4E7A33] hover:underline flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${suggestLoading ? 'animate-spin' : ''}`} /> New ideas</button>
          </div>
          {suggestLoading && suggested.length === 0 ? (
            <p className="text-xs text-[#7A6B57]">Thinking up questions for your season…</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {suggested.map((q, i) => (
                <button key={i} onClick={() => { setAskSeed(q); setAskOpen(true); }} data-testid={`qa-suggested-chip-${i}`}
                  className="text-left text-xs font-medium text-[#1A4331] bg-white rounded-full px-3 py-1.5 border border-[#1A4331]/10 hover:border-[#4E7A33] hover:bg-[#FAF6EC] transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* filters */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[['all', 'All'], ['unresolved', 'Unanswered'], ['resolved', 'Solved']].map(([id, l]) => (
          <button key={id} onClick={() => setStateFilter(id)} data-testid={`qa-filter-${id}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${stateFilter === id ? 'bg-[#1A4331] text-white' : 'bg-[#FAF6EC] text-[#1A4331] border border-[#1A4331]/10'}`}>{l}</button>
        ))}
        <button onClick={() => setMine((m) => !m)} data-testid="qa-filter-mine" className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${mine ? 'bg-[#1A4331] text-white' : 'bg-[#FAF6EC] text-[#1A4331] border border-[#1A4331]/10'}`}>My questions</button>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="qa-filter-category" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FAF6EC] text-[#1A4331] border border-[#1A4331]/10 outline-none">
          <option value="all">All topics</option>
          {(meta?.issue_categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      {/* list */}
      {loading ? (
        <div className="py-16 text-center text-[#7A6B57]">Loading questions…</div>
      ) : questions.length === 0 ? (
        <div className="bg-[#FAF6EC] rounded-3xl p-12 text-center border border-[#1A4331]/8" data-testid="qa-empty">
          <MessageCircleQuestion className="w-12 h-12 text-[#8EAA97] mx-auto mb-3" />
          <p className="text-[#1A4331] font-semibold">No questions here yet</p>
          <p className="text-[#7A6B57] text-sm mt-1 mb-4">Ask anything about your garden — growers near you can help with your exact crop and conditions.</p>
          <button onClick={() => setAskOpen(true)} data-testid="qa-empty-ask" className="bg-[#1A4331] text-white font-semibold py-2.5 px-6 rounded-full inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Ask the community</button>
        </div>
      ) : (
        <div className="space-y-2">
          {questions.map((q) => (
            <button key={q.id} onClick={() => setDetailId(q.id)} data-testid={`qa-card-${q.id}`} className="w-full text-left bg-[#FAF6EC] rounded-2xl p-4 border border-[#1A4331]/10 hover:border-[#1A4331]/25 transition-colors">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <StateBadge state={q.state} />
                <span className="text-[10px] font-semibold text-[#7A6B57] bg-[#E5EBDD] px-2 py-0.5 rounded-full uppercase">{q.issue_category}</span>
                {q.crop_name && <span className="text-[10px] font-semibold text-[#4E7A33] bg-[#6B9F4A]/12 px-2 py-0.5 rounded-full">{q.crop_name}</span>}
              </div>
              <p className="font-semibold text-[#1A4331]">{q.title}</p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-[#7A6B57]">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {q.region || '—'}</span>
                <span className="flex items-center gap-1"><MessageCircleQuestion className="w-3 h-3" /> {q.answer_count} {q.answer_count === 1 ? 'answer' : 'answers'}</span>
                {q.reuse_count > 0 && <span className="flex items-center gap-1 text-[#4E7A33]"><HelpCircle className="w-3 h-3" /> helped {q.reuse_count}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {askOpen && <AskModal meta={meta} initialTitle={askSeed} onClose={() => setAskOpen(false)} onCreated={(qid) => { setAskOpen(false); setDetailId(qid); }} />}
    </div>
  );
}

export default CommunityQA;
